import {
	Component,
	OnInit,
	AfterViewInit,
	OnDestroy,
	ViewChild,
	ElementRef,
	Inject,
	PLATFORM_ID,
	HostListener
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import * as THREE from 'three';
import { LayoutService } from '@app/core/services/layout.service';
import { SupabaseService, MessageService } from '@app/core/services';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finalize, tap, switchMap, map } from 'rxjs/operators';
import { from, Observable } from 'rxjs';

interface CaseColor {
	id: string;
	name: string;
	hex: number;
	dbColor: string; // 對應 Supabase MagneticCaseVariants.color 欄位的值（不是品牌命名）
}

// 已加入清單的一筆規格
interface CartLine {
	variantId: number;
	gradeId: string;
	sizeLabel: string;
	colorName: string;
	unitPrice: number;
	quantity: number;
}

interface GradeOption {
	id: string;
	label: string; // 鑑定標籤底色
	cert: string;
}

interface SizeOption {
	id: string;
	txt: string;
	cert: string;
	bar: number; // 縮圖粗細 icon 用
	depth: number; // 對應到 3D 模型的實際厚度
}

// 大阿姨鑑定卡磁鐵殼 - 商品展示原型
// 拖曳可 360° 旋轉，切換鑑定類型/尺寸/顏色會即時反映在 3D 模型上。
// 目前僅為展示用原型：價格、庫存、加入購物車皆尚未串接 Supabase，之後確定視覺方向後再往下接。
@Component({
	selector: 'app-magnetic-case',
	templateUrl: './magnetic-case.component.html',
	styleUrls: ['./magnetic-case.component.scss']
})
export class MagneticCaseComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('stageEl') stageRef: ElementRef<HTMLDivElement>;
	@ViewChild('canvasEl') canvasRef: ElementRef<HTMLCanvasElement>;

	isBrowser: boolean;
	threeReady = true; // CDN/套件載入失敗時切成 false，顯示 fallback 文字
	showHint = true;

	colors: CaseColor[] = [
		{ id: 'black', name: '曜石黑', hex: 0x0a0a0a, dbColor: '黑色' },
		{ id: 'orange', name: '熾焰橘', hex: 0xcf5912, dbColor: '橘色' },
		{ id: 'silver', name: '星辰銀', hex: 0xc9ced4, dbColor: '銀色' },
		{ id: 'gold', name: '香檳金', hex: 0xd9ab3c, dbColor: '金色' },
		{ id: 'green', name: '墨玉綠', hex: 0x173129, dbColor: '墨綠' }
	];

	grades: GradeOption[] = [
		{ id: 'PSA', label: '#c1272d', cert: '紅標鑑定' },
		{ id: 'BGS', label: '#0d0d0f', cert: '黑金鑑定' },
		{ id: 'CGC', label: '#1f4fa3', cert: '藍標鑑定' }
	];

	sizes: SizeOption[] = [
		{ id: '35pt', txt: '35pt', cert: '標準厚度', bar: 6, depth: 0.12 },
		{ id: '130pt', txt: '130pt', cert: '加厚球卡', bar: 18, depth: 0.24 }
	];

	selectedGrade = this.grades[0];
	selectedSize = this.sizes[0];
	selectedColor = this.colors[0];

	// 從 Supabase 抓回來的規格庫存/價格清單
	variants: any[] = [];
	// 目前要加入清單的數量
	quantity = 1;
	// 已加入清單、尚未送出的規格明細
	cartLines: CartLine[] = [];

	mode: 'list' | 'success' = 'list'; // list：選規格+清單 || success：訂購成功畫面

	buyerForm: FormGroup = this.formBuilder.group({
		buyerName: ['', Validators.required],
		buyerPhone: ['', Validators.required],
		buyerEmail: ['', [Validators.required, Validators.email]],
		buyerBankAccount: ['', Validators.required],
		'711Name': ['', Validators.required]
	});

	// ---- three.js 內部狀態 ----
	private renderer: THREE.WebGLRenderer;
	private scene: THREE.Scene;
	private camera: THREE.PerspectiveCamera;
	private caseGroup: THREE.Group;
	private frameMesh: THREE.Mesh;
	private glassMesh: THREE.Mesh;
	private cardMesh: THREE.Mesh;
	private frameMaterial: THREE.MeshPhysicalMaterial;
	private glassMaterial: THREE.MeshPhysicalMaterial;
	private cardMaterial: THREE.MeshStandardMaterial;
	private cardTex: THREE.CanvasTexture;
	private animationId: number;

	private readonly W = 2.1;
	private readonly H = 3.7;
	private readonly R = 0.18;
	private readonly WIN_INSET = 0.16;

	private dragging = false;
	private lastX = 0;
	private lastY = 0;
	private velocity = 0;

	private colorTween: { from: THREE.Color; to: THREE.Color; t: number } | null = null;
	private reduceMotion = false;
	private hintTimer: any;

	constructor(
		@Inject(DOCUMENT) private document: Document,
		@Inject(PLATFORM_ID) private platformId,
		public layoutService: LayoutService,
		private supabaseService: SupabaseService,
		private formBuilder: FormBuilder,
		public messageService: MessageService
	) {
		this.isBrowser = isPlatformBrowser(this.platformId);
	}

	ngOnInit(): void {
		const canonical = this.document.querySelector('link[rel="canonical"]') as HTMLAnchorElement | null;
		if (canonical) canonical.href = 'https://bigaunt666.github.io/bigaunt/magnetic-case';

		this.layoutService.setPageLoading(true);
		this.getVariants$()
			.pipe(
				switchMap(() => this.getSession$()),
				tap((sessionRes) => this.setUserData(sessionRes)),
				finalize(() => this.layoutService.setPageLoading(false))
			)
			.subscribe();
	}

	ngAfterViewInit(): void {
		if (!this.isBrowser) return;
		try {
			this.reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
			this.initThree();
			this.hintTimer = setTimeout(() => (this.showHint = false), 4000);
		} catch (err) {
			console.error('磁鐵殼 3D 預覽初始化失敗', err);
			this.threeReady = false;
		}
	}

	ngOnDestroy(): void {
		if (this.animationId) cancelAnimationFrame(this.animationId);
		clearTimeout(this.hintTimer);
		if (this.frameMesh) this.frameMesh.geometry.dispose();
		if (this.glassMesh) this.glassMesh.geometry.dispose();
		if (this.cardMesh) this.cardMesh.geometry.dispose();
		if (this.frameMaterial) this.frameMaterial.dispose();
		if (this.glassMaterial) this.glassMaterial.dispose();
		if (this.cardMaterial) this.cardMaterial.dispose();
		if (this.cardTex) this.cardTex.dispose();
		if (this.renderer) this.renderer.dispose();
	}

	// ---------------------------------------------------------------------
	// UI 互動
	// ---------------------------------------------------------------------

	selectGrade(g: GradeOption): void {
		this.selectedGrade = g;
	}

	selectSize(s: SizeOption): void {
		this.selectedSize = s;
		if (this.isBrowser) this.rebuildCase(s.depth);
	}

	selectColor(c: CaseColor): void {
		this.selectedColor = c;
		if (this.isBrowser) this.startColorTween(c.hex);
	}

	hexLabel(hex: number): string {
		return '#' + hex.toString(16).toUpperCase().padStart(6, '0');
	}

	swatchBg(hex: number): string {
		return '#' + hex.toString(16).padStart(6, '0');
	}

	// PSA、CGC 共用同一組庫存，BGS 是另一組（沒有分尺寸）
	getVariantType(gradeId: string): string {
		return gradeId === 'BGS' ? 'BGS' : 'PSA/CGC';
	}

	// 目前選擇（類型+尺寸+顏色）對應到的庫存規格列，查不到就是 undefined
	get currentVariant(): any {
		const type = this.getVariantType(this.selectedGrade.id);
		const size = type === 'BGS' ? null : this.selectedSize.id;
		return this.variants.find((v) => v.type === type && v.size === size && v.color === this.selectedColor.dbColor);
	}

	// 這個規格扣掉「已經加入清單但還沒送出」的數量後，實際還能加的庫存
	get availableStock(): number {
		const variant = this.currentVariant;
		if (!variant) return 0;
		const inCart = this.cartLines
			.filter((l) => l.variantId === variant.id)
			.reduce((sum, l) => sum + l.quantity, 0);
		return Math.max(0, variant.stock - inCart);
	}

	// 運費固定 60 元（不分規格、不分件數，一張訂單收一次）
	readonly shippingFee = 60;

	get cartTotal(): number {
		return this.cartLines.reduce((sum, l) => sum + l.unitPrice * l.quantity, 0);
	}

	get grandTotal(): number {
		return this.cartTotal + this.shippingFee;
	}

	changeQuantity(delta: number): void {
		const next = this.quantity + delta;
		this.quantity = Math.max(1, Math.min(next, this.availableStock || 1));
	}

	// 把目前選擇的規格 + 數量加進清單
	addToCartLine(): void {
		const variant = this.currentVariant;
		if (!variant) {
			this.messageService.add({ severity: 'error', summary: '注意', detail: '查無此規格，請重新選擇', life: 4000 });
			return;
		}
		if (this.quantity > this.availableStock) {
			this.messageService.add({
				severity: 'error',
				summary: '庫存不足',
				detail: `這個規格目前只剩 ${this.availableStock} 個`,
				life: 4000
			});
			return;
		}

		const existing = this.cartLines.find((l) => l.variantId === variant.id);
		if (existing) existing.quantity += this.quantity;
		else {
			this.cartLines.push({
				variantId: variant.id,
				gradeId: this.selectedGrade.id,
				sizeLabel: variant.size || '單一規格',
				colorName: this.selectedColor.name,
				unitPrice: variant.price,
				quantity: this.quantity
			});
		}

		this.messageService.add({ severity: 'success', summary: '已加入', detail: '已加入清單', life: 2000 });
		this.quantity = 1;
	}

	removeCartLine(index: number): void {
		this.cartLines.splice(index, 1);
	}

	// - 送出訂單（可能包含多筆規格），成功與否都由後端 RPC 說了算
	submitOrder(): void {
		if (this.cartLines.length <= 0) {
			this.messageService.add({ severity: 'error', summary: '注意', detail: '清單是空的，請先加入商品', life: 4000 });
			return;
		}
		if (this.buyerForm.invalid) {
			this.messageService.add({ severity: 'error', summary: '注意', detail: '請填寫完整收件資料', life: 4000 });
			return;
		}

		const orderId = this.generateUUIDv4();
		const items = this.cartLines.map((l) => ({ variantId: l.variantId, quantity: l.quantity }));
		const buyer = this.buyerForm.getRawValue();

		this.layoutService.setPageLoading(true);
		from(this.supabaseService.submitMagneticCaseOrder(orderId, items, buyer))
			.pipe(finalize(() => this.layoutService.setPageLoading(false)))
			.subscribe({
				next: ({ data, error }: any) => {
					if (error || data !== true) {
						this.messageService.add({
							severity: 'error',
							summary: '訂購失敗',
							detail: '可能是庫存剛好被搶完了，請重新確認清單數量',
							life: 5000
						});
						this.getVariants$().subscribe();
						return;
					}
					this.cartLines = [];
					this.mode = 'success';
				},
				error: (err) => {
					console.error('Unexpected error:', err);
					this.messageService.add({
						severity: 'error',
						summary: '系統錯誤',
						detail: '請聯絡大阿姨團隊',
						life: 5000
					});
				}
			});
	}

	generateUUIDv4(): string {
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
			const r = (Math.random() * 16) | 0;
			const v = c === 'x' ? r : (r & 0x3) | 0x8;
			return v.toString(16);
		});
	}

	// - 取得登入狀態
	getSession$(): Observable<any> {
		return from(this.supabaseService.getSession()).pipe(
			map(({ data, error }) => {
				if (error) throw error;
				return data;
			})
		);
	}

	// 如果有登入，帶入 localStorage 存的個人資料（跟團拆頁面「個人資訊」邏輯一致）
	setUserData(sessionRes): void {
		if (!sessionRes.session || !this.isBrowser) return;
		const raw = localStorage.getItem('profile');
		if (!raw) return;
		const profile = JSON.parse(raw);
		this.buyerForm.patchValue({
			buyerName: profile.name,
			buyerPhone: profile.phone,
			buyerEmail: profile.email,
			buyerBankAccount: profile.bankAccount,
			'711Name': profile['711Name']
		});
	}

	// - 取得所有規格的庫存/價格
	getVariants$(): Observable<any> {
		return from(this.supabaseService.getMagneticCaseVariants()).pipe(
			tap(({ data, error }: any) => {
				if (error) {
					console.error('取得磁鐵殼庫存失敗:', error);
					return;
				}
				this.variants = data || [];
			})
		);
	}

	// ---------------------------------------------------------------------
	// 拖曳旋轉
	// ---------------------------------------------------------------------

	onPointerDown(e: PointerEvent): void {
		(e.target as HTMLElement).setPointerCapture(e.pointerId);
		this.dragging = true;
		this.lastX = e.clientX;
		this.lastY = e.clientY;
		this.showHint = false;
	}

	onPointerMove(e: PointerEvent): void {
		if (!this.dragging || !this.caseGroup) return;
		const dx = e.clientX - this.lastX;
		const dy = e.clientY - this.lastY;
		this.caseGroup.rotation.y += dx * 0.012;
		this.caseGroup.rotation.x = Math.max(-0.5, Math.min(0.5, this.caseGroup.rotation.x + dy * 0.008));
		this.velocity = dx * 0.012;
		this.lastX = e.clientX;
		this.lastY = e.clientY;
	}

	onPointerUp(): void {
		this.dragging = false;
	}

	@HostListener('window:resize')
	onWindowResize(): void {
		if (!this.isBrowser || !this.renderer || !this.stageRef) return;
		this.resizeRenderer();
	}

	// ---------------------------------------------------------------------
	// three.js 場景
	// ---------------------------------------------------------------------

	private initThree(): void {
		const canvas = this.canvasRef.nativeElement;

		this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
		this.renderer.outputEncoding = THREE.sRGBEncoding;
		this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
		this.renderer.toneMappingExposure = 1.05;

		this.scene = new THREE.Scene();
		this.camera = new THREE.PerspectiveCamera(28, 1, 0.1, 100);
		this.camera.position.set(0, 0.1, 9.8);

		// 用程式畫的漸層當環境貼圖，做出金屬邊框的柔和反光，不需要外部素材
		const envCanvas = document.createElement('canvas');
		envCanvas.width = 8;
		envCanvas.height = 256;
		const ectx = envCanvas.getContext('2d');
		const grad = ectx.createLinearGradient(0, 0, 0, 256);
		grad.addColorStop(0, '#ffffff');
		grad.addColorStop(0.45, '#c7cfd6');
		grad.addColorStop(1, '#40474f');
		ectx.fillStyle = grad;
		ectx.fillRect(0, 0, 8, 256);
		const envTex = new THREE.CanvasTexture(envCanvas);
		envTex.mapping = THREE.EquirectangularReflectionMapping;
		this.scene.environment = envTex;

		this.scene.add(new THREE.HemisphereLight(0xffffff, 0x4a4f57, 0.55));
		const key = new THREE.DirectionalLight(0xffffff, 0.9);
		key.position.set(3.5, 4, 5);
		this.scene.add(key);
		const fill = new THREE.DirectionalLight(0xffffff, 0.35);
		fill.position.set(-4, -1.5, 3);
		this.scene.add(fill);
		const rim = new THREE.DirectionalLight(0xbfd4e8, 0.5);
		rim.position.set(-2, 3, -4);
		this.scene.add(rim);

		this.caseGroup = new THREE.Group();
		this.caseGroup.position.y = 0.22; // 整體往上移一點，讓下方留白多一點，畫面不會貼太近底部
		this.scene.add(this.caseGroup);

		this.cardTex = this.buildCardTexture();

		this.frameMaterial = new THREE.MeshPhysicalMaterial({
			color: this.selectedColor.hex,
			metalness: 0.92,
			roughness: 0.28,
			clearcoat: 0.35,
			clearcoatRoughness: 0.22,
			envMapIntensity: 1.15
		});
		this.glassMaterial = new THREE.MeshPhysicalMaterial({
			color: 0xf3f7fb,
			metalness: 0,
			roughness: 0.05,
			transmission: 0.92,
			ior: 1.45,
			envMapIntensity: 1
		});
		// @types/three 0.128 沒收錄 thickness 欄位，但 three.js 執行期支援，用型別斷言補上
		(this.glassMaterial as any).thickness = 0.4;
		this.cardMaterial = new THREE.MeshStandardMaterial({ map: this.cardTex, roughness: 0.85, metalness: 0 });

		this.buildCase(this.selectedSize.depth);
		this.resizeRenderer();
		this.animate();
	}

	private roundedRectShape(w: number, h: number, r: number): THREE.Shape {
		const s = new THREE.Shape();
		const x = -w / 2;
		const y = -h / 2;
		s.moveTo(x, y + r);
		s.lineTo(x, y + h - r);
		s.quadraticCurveTo(x, y + h, x + r, y + h);
		s.lineTo(x + w - r, y + h);
		s.quadraticCurveTo(x + w, y + h, x + w, y + h - r);
		s.lineTo(x + w, y + r);
		s.quadraticCurveTo(x + w, y, x + w - r, y);
		s.lineTo(x + r, y);
		s.quadraticCurveTo(x, y, x, y + r);
		return s;
	}

	private buildCardTexture(): THREE.CanvasTexture {
		const c = document.createElement('canvas');
		c.width = 340;
		c.height = 600;
		const ctx = c.getContext('2d');
		ctx.fillStyle = '#f4f6f8';
		ctx.fillRect(0, 0, c.width, c.height);
		ctx.strokeStyle = '#c7ced6';
		ctx.lineWidth = 6;
		ctx.strokeRect(10, 10, c.width - 20, c.height - 20);
		ctx.fillStyle = '#aab4bf';
		ctx.font = "700 26px 'Noto Sans TC', sans-serif";
		ctx.textAlign = 'center';
		ctx.fillText('SAMPLE CARD', c.width / 2, c.height / 2 - 10);
		ctx.font = '500 16px monospace';
		ctx.fillStyle = '#c2cad2';
		ctx.fillText("BIG AUNT'S GRADING", c.width / 2, c.height / 2 + 22);
		const tex = new THREE.CanvasTexture(c);
		tex.encoding = THREE.sRGBEncoding;
		return tex;
	}

	private buildCase(depth: number): void {
		if (this.frameMesh) {
			this.caseGroup.remove(this.frameMesh, this.glassMesh, this.cardMesh);
			this.frameMesh.geometry.dispose();
			this.glassMesh.geometry.dispose();
			this.cardMesh.geometry.dispose();
		}

		const outer = this.roundedRectShape(this.W, this.H, this.R);
		const hole = this.roundedRectShape(this.W - this.WIN_INSET * 2, this.H - this.WIN_INSET * 2, this.R * 0.6);
		outer.holes.push(hole);

		const frameGeo = new THREE.ExtrudeGeometry(outer, {
			depth,
			bevelEnabled: true,
			bevelThickness: 0.045,
			bevelSize: 0.045,
			bevelSegments: 4,
			curveSegments: 24
		});
		frameGeo.center();

		this.frameMesh = new THREE.Mesh(frameGeo, this.frameMaterial);
		this.caseGroup.add(this.frameMesh);

		const glassGeo = new THREE.PlaneGeometry(this.W - this.WIN_INSET * 2 - 0.05, this.H - this.WIN_INSET * 2 - 0.05);
		this.glassMesh = new THREE.Mesh(glassGeo, this.glassMaterial);
		this.glassMesh.position.z = depth / 2 - 0.01;
		this.caseGroup.add(this.glassMesh);

		const cardGeo = new THREE.PlaneGeometry(this.W - this.WIN_INSET * 2 - 0.16, this.H - this.WIN_INSET * 2 - 0.16);
		this.cardMesh = new THREE.Mesh(cardGeo, this.cardMaterial);
		this.cardMesh.position.z = depth / 2 - 0.05;
		this.caseGroup.add(this.cardMesh);
	}

	private rebuildCase(depth: number): void {
		this.buildCase(depth);
	}

	private startColorTween(targetHex: number): void {
		this.colorTween = {
			from: this.frameMaterial.color.clone(),
			to: new THREE.Color(targetHex),
			t: 0
		};
	}

	private resizeRenderer(): void {
		const stage = this.stageRef.nativeElement;
		const w = stage.clientWidth;
		const h = stage.clientHeight;
		if (!w || !h) return;
		this.renderer.setSize(w, h, false);
		this.camera.aspect = w / h;
		this.camera.updateProjectionMatrix();
	}

	private animate = (): void => {
		this.animationId = requestAnimationFrame(this.animate);

		if (!this.dragging && this.caseGroup) {
			if (Math.abs(this.velocity) > 0.0002) {
				this.caseGroup.rotation.y += this.velocity;
				this.velocity *= 0.94;
			} else if (!this.reduceMotion) {
				this.caseGroup.rotation.y += 0.0028;
			}
		}

		if (this.colorTween) {
			this.colorTween.t += 0.045;
			if (this.colorTween.t >= 1) {
				this.frameMaterial.color.copy(this.colorTween.to);
				this.colorTween = null;
			} else {
				this.frameMaterial.color.copy(this.colorTween.from).lerp(this.colorTween.to, this.colorTween.t);
			}
		}

		this.renderer.render(this.scene, this.camera);
	};
}
